import React from "react"
import { CalendarSettingsIdInput } from "../../../calendar-settings/api/calendar-settings-id"
import { useContextHolidays } from "../../../hooks/use-context-holidays"
import { useCalendarSettings } from "../../../hooks/use-calendar-settings"
import { ProjectsViewOptions } from "../../../projects/components/ProjectsViewSelect"
import { useGetProjectsQuery } from "../../../projects/api"
import { AppMainContextType, AppSubContextType } from "../../../types/app-context"
import { makeProjectListQuery } from "../utils/project-list-query"

const queryOptions = { refetchOnMountOrArgChange: true } as const

export const useProjectListRouteData = ({
  context,
  mainContext,
  showProjectList,
  view,
}: UseProjectListRouteDataProps) => {
  const query = makeProjectListQuery(context, view)
  const { data: projects = [], isLoading, isError, status } = useGetProjectsQuery(query, queryOptions)
  const projectCalendarSettingsIds = React.useMemo<CalendarSettingsIdInput[]>(
    () => projects.map((project) => ({ type: "project", id: project.id })),
    [projects]
  )
  const shouldSkipProjectCalendarSettingsIds = projectCalendarSettingsIds.length === 0 || !showProjectList
  const {
    calendarSettingsById,
    firstDayOfWeek,
    weekendDays,
    settingsKey,
    isLoading: isLoadingProjectCalendarSettings,
    isError: isErrorProjectCalendarSettings,
    queryErrors: calendarSettingsQueryErrors,
  } = useCalendarSettings({
    mainContext,
    calendarSettingsIds: projectCalendarSettingsIds,
    skipCalendarSettingsIds: shouldSkipProjectCalendarSettingsIds,
    skipAll: !mainContext || status !== "fulfilled",
  })
  const {
    holidays,
    isLoading: isLoadingHolidays,
    isError: isErrorHolidays,
    queryErrors: holidaysQueryErrors,
  } = useContextHolidays(mainContext)

  return {
    calendarSettingsById,
    calendarSettingsQueryErrors,
    firstDayOfWeek,
    holidays,
    holidaysQueryErrors,
    isError,
    isErrorHolidays,
    isErrorProjectCalendarSettings,
    isLoading,
    isLoadingHolidays,
    isLoadingProjectCalendarSettings,
    projects,
    query,
    settingsKey,
    status,
    weekendDays,
  }
}

type UseProjectListRouteDataProps = {
  context: AppMainContextType | AppSubContextType | null
  mainContext: AppMainContextType | null
  showProjectList: boolean
  view: ProjectsViewOptions
}
